import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowDown, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useDiagnostic } from "@/context/DiagnosticContext";
import { MaskedLines, Reveal } from "@/components/site/Motion";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Chatbot from "@/components/site/Chatbot";
import SectorSelector from "@/components/site/SectorSelector";
import MetricsPreview from "@/components/site/MetricsPreview";
import CTABand from "@/components/site/CTABand";
import FAQ from "@/components/site/FAQ";

const HERO_IMG = "https://images.unsplash.com/photo-1609048647061-76d541aba9f6";
const STORY_IMG = "https://images.unsplash.com/photo-1600878459138-e1123b37cb30";

function Hero({ content }) {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const { openDiagnostic } = useDiagnostic();
  const h = content?.hero || {};
  const heroImg = content?.images?.hero || HERO_IMG;
  return (
    <section className="relative pt-40 md:pt-52 pb-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
            className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-faint mb-8"
          >
            {h.eyebrow}
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] text-ink">
            <MaskedLines lines={h.title_lines || []} delay={0.3} />
          </h1>
          <Reveal delay={0.9} className="mt-10 max-w-xl">
            <p className="font-sans text-base md:text-lg text-subtle leading-relaxed">{h.subtitle}</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button
                data-testid="hero-cta-primary"
                onClick={() => openDiagnostic()}
                className="group flex items-center gap-3 px-7 py-4 rounded-full bg-ink text-stone-50 font-sans text-sm hover:bg-black transition-colors duration-300"
              >
                {h.cta_primary}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => document.getElementById("servicios")?.scrollIntoView({ behavior: "smooth" })}
                className="px-7 py-4 rounded-full border border-ink/20 text-ink font-sans text-sm hover:border-ink transition-colors duration-300"
              >
                {h.cta_secondary}
              </button>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-4">
          <motion.div style={{ y }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
              className="rounded-t-[180px] overflow-hidden aspect-[3/4] bg-stone-100"
            >
              <motion.img
                src={heroImg} alt="Gustavo D'Ommar"
                initial={{ scale: 1.2 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
        className="mt-16 flex items-center gap-3 text-faint"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="font-sans text-xs tracking-[0.2em] uppercase">Desliza para descubrir</span>
      </motion.div>
    </section>
  );
}

function Positioning({ content }) {
  const lines = content?.positioning?.lines || [];
  return (
    <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <div className="max-w-4xl">
        {lines.map((line, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <p className={`font-serif text-3xl md:text-5xl leading-[1.15] tracking-tight ${i === lines.length - 1 ? "text-ink" : "text-faint"}`}>
              {line}
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Manifesto({ services }) {
  return (
    <section id="servicios" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <Reveal>
        <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-4">Nuestro servicio integral</h4>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink max-w-3xl leading-[1.05]">AI Digital Growth Partner</h2>
        <p className="font-sans text-base md:text-lg text-subtle mt-6 max-w-xl">Un acompañamiento mensual donde dispones de un equipo que utiliza IA para hacer crecer tu negocio.</p>
      </Reveal>

      <div className="mt-16 md:mt-24">
        {services.map((s, i) => (
          <Reveal key={s.id} delay={0.05}>
            <div data-testid={`service-${s.number}`} className="group grid md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-t border-border items-start">
              <div className="md:col-span-2">
                <span className="font-sans text-5xl md:text-7xl font-light text-stone-200 group-hover:text-ink transition-colors duration-500">{s.number}</span>
              </div>
              <div className="md:col-span-4">
                <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight">{s.title}</h3>
                <p className="font-sans text-sm md:text-base text-subtle mt-3">{s.description}</p>
              </div>
              <div className="md:col-span-6 flex flex-wrap gap-2 content-start">
                {s.items?.map((it, k) => (
                  <span key={k} className="font-sans text-xs md:text-sm px-3 py-1.5 rounded-full border border-border text-subtle">{it}</span>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function EditorialMarquee({ content }) {
  const text = content?.marquee_text || "DOMMAR STUDIO ·";
  return (
    <section className="py-16 md:py-24 border-t border-border overflow-hidden">
      <Marquee speed={40} gradient={false} autoFill>
        <span className="font-serif italic text-6xl md:text-8xl lg:text-9xl outline-text px-6">{text}</span>
      </Marquee>
    </section>
  );
}

function Audience({ content }) {
  const a = content?.audience || {};
  return (
    <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <Reveal><h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink mb-16">{a.heading}</h2></Reveal>
      <div className="grid md:grid-cols-2 gap-16">
        <Reveal>
          <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-8">Profesionales independientes</h4>
          <div className="flex flex-wrap gap-3">
            {a.professionals?.map((p, i) => (
              <span key={i} className="font-serif text-2xl md:text-3xl text-ink border-b border-transparent hover:border-ink transition-colors duration-300">{p}{i < a.professionals.length - 1 ? "," : "."}</span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-8">Empresas</h4>
          <div className="flex flex-wrap gap-3">
            {a.companies?.map((c, i) => (
              <span key={i} className="font-serif text-2xl md:text-3xl text-ink border-b border-transparent hover:border-ink transition-colors duration-300">{c}{i < a.companies.length - 1 ? "," : "."}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FounderStory({ content }) {
  const f = content?.founder || {};
  const founderImg = content?.images?.founder || STORY_IMG;
  const { scrollYProgress } = useScroll();
  return (
    <section id="nosotros" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="overflow-hidden aspect-[4/5] bg-stone-100 rounded-sm">
              <motion.img
                src={founderImg} alt={f.name}
                initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal>
            <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-4">{f.role}</h4>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink mb-8">{f.heading}</h2>
            {f.paragraphs?.map((p, i) => (
              <p key={i} className="font-sans text-base md:text-lg text-subtle leading-relaxed mb-5 max-w-xl">{p}</p>
            ))}
            <p className="font-serif italic text-2xl md:text-3xl text-ink mt-8">— {f.name}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Pricing({ plans, content }) {
  const d = content?.differentiator || {};
  const { openDiagnostic } = useDiagnostic();
  return (
    <section id="planes" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <Reveal>
        <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-4">Modelo de negocio</h4>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink max-w-2xl">Un socio, no un proveedor.</h2>
      </Reveal>

      <div className="grid md:grid-cols-3 gap-6 mt-16">
        {plans.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <div
              data-testid={`plan-${p.name}`}
              className={`h-full flex flex-col p-8 rounded-lg border transition-transform duration-500 hover:-translate-y-1 ${
                p.highlight ? "border-ink bg-white" : "border-border bg-white"
              }`}
            >
              {p.highlight && <span className="self-start font-sans text-[10px] tracking-[0.2em] uppercase px-3 py-1 rounded-full bg-ink text-stone-50 mb-6">Ideal</span>}
              <h3 className="font-sans text-sm tracking-[0.2em] uppercase text-faint">{p.name}</h3>
              <p className="font-serif text-2xl text-subtle mt-1">{p.target}</p>
              <div className="flex items-baseline gap-1 mt-6">
                <span className="font-serif text-5xl md:text-6xl text-ink">{p.price}</span>
                <span className="font-sans text-sm text-faint">/mes</span>
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {p.features?.map((f, k) => (
                  <li key={k} className="flex items-start gap-3 font-sans text-sm text-subtle">
                    <Check size={16} className="text-ink mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`plan-cta-${p.name}`}
                onClick={() => openDiagnostic()}
                className={`mt-8 w-full py-3.5 rounded-full font-sans text-sm transition-colors duration-300 ${
                  p.highlight ? "bg-ink text-stone-50 hover:bg-black" : "border border-ink/20 text-ink hover:border-ink"
                }`}
              >
                Empezar
              </button>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-24 max-w-3xl">
          <h4 className="font-sans text-sm tracking-[0.2em] uppercase text-faint mb-4">{d.heading}</h4>
          <p className="font-serif text-3xl md:text-4xl leading-[1.2] text-ink">{d.text}</p>
        </div>
      </Reveal>
    </section>
  );
}

function Contact({ content }) {
  const c = content?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Completa nombre, email y mensaje."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Gracias, te contactaremos pronto.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al enviar.");
    } finally { setSending(false); }
  };

  const inp = "w-full bg-transparent border-b border-border py-4 font-sans text-lg text-ink placeholder:text-faint outline-none focus:border-ink transition-colors duration-300";

  return (
    <section id="contacto" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-border">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-ink leading-[1.05]">{c.heading}</h2>
            <p className="font-sans text-base md:text-lg text-subtle mt-6 max-w-md">{c.subtitle}</p>
            {c.email && <a href={`mailto:${c.email}`} className="inline-block font-serif text-2xl text-ink mt-10 border-b border-ink/20 hover:border-ink transition-colors duration-300">{c.email}</a>}
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <input data-testid="contact-name" className={inp} placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="contact-email" type="email" className={inp} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <input data-testid="contact-phone" className={inp} placeholder="Teléfono (opcional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea data-testid="contact-message" rows={4} className={inp + " resize-none"} placeholder="Cuéntanos sobre tu proyecto" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              <button data-testid="contact-submit" disabled={sending} className="group flex items-center gap-3 px-8 py-4 rounded-full bg-ink text-stone-50 font-sans text-sm hover:bg-black transition-colors duration-300 disabled:opacity-50">
                {sending ? "Enviando…" : "Enviar solicitud"}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/content"), api.get("/services"), api.get("/plans")])
      .then(([c, s, p]) => { setContent(c.data); setServices(s.data); setPlans(p.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (content && window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [content]);

  if (!content) return <div className="min-h-screen bg-stone-50" />;

  return (
    <div className="bg-stone-50 min-h-screen">
      <Navbar />
      <main>
        <Hero content={content} />
        <Positioning content={content} />
        <SectorSelector content={content} />
        <Manifesto services={services} />
        <EditorialMarquee content={content} />
        <MetricsPreview content={content} />
        <Audience content={content} />
        <FounderStory content={content} />
        <Pricing plans={plans} content={content} />
        <FAQ content={content} />
        <CTABand content={content} />
        <Contact content={content} />
      </main>
      <Footer content={content} />
      <Chatbot content={content} />
    </div>
  );
}
